import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Trophy, Sparkles, Lock, Award } from "lucide-react";
import { useAuth } from "../lib/auth";
import { htmlToPlainText } from "../lib/text";

/**
 * Pagina "I miei livelli". Mostra tutti i livelli configurati dall'admin con:
 *  - badge sbloccati (colore pieno, data di sblocco)
 *  - badge da sbloccare (grigio, con barra di progresso + soglia rimanente)
 * Nessun redeem: gli sblocchi avvengono automaticamente a fine caccia in base a
 * `user.total_points` (vedi backend /rewards).
 */
export default function Rewards() {
  const { user } = useAuth();
  const [levels, setLevels] = useState(null);

  useEffect(() => {
    api.get("/rewards").then((r) => setLevels(r.data)).catch(() => setLevels([]));
  }, []);

  const totalPoints = user?.total_points || 0;
  const unlockedCount = (levels || []).filter((l) => l.unlocked).length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-gold flex items-center justify-center shadow-tactile">
            <Trophy className="text-ink-900" size={26}/>
          </div>
          <div>
            <h1 className="font-display font-extrabold text-3xl text-ink-900" data-testid="levels-title">I tuoi livelli</h1>
            <p className="text-ink-500 text-sm">
              Guadagna punti nelle cacce al tesoro per sbloccare nuovi badge
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge className="bg-gold text-ink-900 rounded-full px-4 py-2 font-bold border-0 text-base" data-testid="levels-points">
            <Sparkles size={14} className="mr-1"/> {totalPoints} pt
          </Badge>
          <Badge className="bg-forest text-white rounded-full px-4 py-2 font-bold border-0 text-base" data-testid="levels-count">
            <Award size={14} className="mr-1"/> {unlockedCount}/{levels?.length || 0}
          </Badge>
        </div>
      </div>

      {levels === null ? (
        <div className="text-center text-ink-500 py-10 text-sm">Caricamento...</div>
      ) : levels.length === 0 ? (
        <Card className="card-treasure p-8 text-center" data-testid="levels-empty">
          <Trophy className="mx-auto text-ink-400 mb-3" size={48}/>
          <p className="text-ink-700 font-bold">Nessun livello ancora disponibile</p>
          <p className="text-ink-500 text-sm mt-1">
            L'amministratore non ha ancora configurato i badge. Torna presto!
          </p>
        </Card>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {levels.map((lv) => (
            <LevelCard key={lv.reward_id} level={lv} totalPoints={totalPoints} />
          ))}
        </div>
      )}
    </div>
  );
}

function LevelCard({ level, totalPoints }) {
  const missing = Math.max(0, level.points_threshold - totalPoints);
  return (
    <Card
      className={`card-treasure overflow-hidden p-0 transition ${level.unlocked ? "" : "opacity-90"}`}
      data-testid={`level-${level.reward_id}`}
    >
      <div className={`relative h-40 flex items-center justify-center ${level.unlocked ? "bg-gold-light" : "bg-parchment-dark"}`}>
        {level.image_url ? (
          <img
            src={level.image_url}
            alt={level.name}
            className={`absolute inset-0 w-full h-full object-cover ${level.unlocked ? "" : "grayscale opacity-60"}`}
          />
        ) : (
          <div className={`text-7xl ${level.unlocked ? "" : "grayscale opacity-40"}`} aria-hidden="true">
            {level.emoji || "🏆"}
          </div>
        )}
        {!level.unlocked && (
          <div className="absolute top-2 right-2">
            <Badge className="bg-white/90 rounded-full font-bold text-ink-700 border-0 gap-1">
              <Lock size={12}/> Bloccato
            </Badge>
          </div>
        )}
        {level.unlocked && (
          <div className="absolute top-2 right-2">
            <Badge className="bg-forest text-white rounded-full font-bold border-0 gap-1" data-testid={`level-unlocked-badge-${level.reward_id}`}>
              <Sparkles size={12}/> Sbloccato
            </Badge>
          </div>
        )}
      </div>
      <div className="p-5">
        <h3 className="font-display font-bold text-xl text-ink-900">
          <span className="mr-1">{level.emoji || "🏆"}</span>{level.name}
        </h3>
        {level.description && (
          <p className="text-sm text-ink-700 mt-1 mb-3 line-clamp-2" data-testid={`level-description-${level.reward_id}`}>
            {htmlToPlainText(level.description)}
          </p>
        )}
        <div className="mt-3">
          <div className="flex items-center justify-between text-xs font-bold mb-1">
            <span className="text-ink-500">Soglia</span>
            <span className="text-gold-hover font-display text-base" data-testid={`level-threshold-${level.reward_id}`}>{level.points_threshold} pt</span>
          </div>
          <div className="w-full h-2 rounded-full bg-parchment-dark overflow-hidden">
            <div
              className={`h-full ${level.unlocked ? "bg-forest" : "bg-gold"}`}
              style={{ width: `${level.progress_pct}%` }}
              data-testid={`level-progress-${level.reward_id}`}
            />
          </div>
          {!level.unlocked && (
            <p className="text-[11px] text-ink-500 mt-1 text-right">
              Ti mancano <strong className="text-gold-hover">{missing} pt</strong>
            </p>
          )}
          {level.unlocked && level.unlocked_at && (
            <p className="text-[11px] text-forest mt-1 text-right font-bold">
              Sbloccato il {new Date(level.unlocked_at).toLocaleDateString("it-IT")}
            </p>
          )}
        </div>
      </div>
    </Card>
  );
}
